// covered-by: qa/rickrubin/tests/specs/cca/ask.puntt.campaign.spec.ts
import { type AskPunttProjectScope } from "@mg/schemas/src/christo/askPunttProject";
import { CircleNotch, FolderSimplePlus, Plus } from "@phosphor-icons/react";
import {
  Button,
  Callout,
  Dialog,
  Flex,
  SegmentedControl,
  Text,
  TextArea,
  TextField,
} from "@radix-ui/themes";
import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";

import { campaignRoute } from "./campaignRoute";

import { useAnalytics } from "../../utils/analytics";
import {
  useAskPunttProjects,
  useCreateAskPunttProject,
} from "../../utils/queries/askPunttProjects";

function formatDate(iso: string | Date | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export default function AskPunttCampaigns() {
  const navigate = useNavigate();
  const analytics = useAnalytics("ask_puntt_campaigns");
  const [scope, setScope] = useState<AskPunttProjectScope>("all");
  const { data, isLoading, isError } = useAskPunttProjects({ scope });
  const createProject = useCreateAskPunttProject();

  const [createOpen, setCreateOpen] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");

  const projects = data?.projects ?? [];

  const handleCreate = async () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    try {
      const result = await createProject.mutateAsync({
        name: trimmed,
        description: description.trim() || undefined,
      });
      analytics.capture("ask_puntt_campaign_created", {
        projectId: result.project._id,
      });
      setName("");
      setDescription("");
      setCreateOpen(false);
      navigate({
        to: campaignRoute.to,
        params: { projectId: result.project._id },
      });
    } catch (error) {
      console.error("Failed to create project:", error);
    }
  };

  return (
    <div
      className="flex h-full flex-col overflow-y-auto"
      data-testid="ask-puntt-campaigns"
    >
      <div className="mx-auto w-full max-w-[960px] px-6 py-8">
        <Flex justify="between" align="center" className="mb-6">
          <Text size="7" weight="bold" className="text-puntt-neutral-gray-12">
            Projects
          </Text>
          <Flex align="center" gap="3">
            <SegmentedControl.Root
              value={scope}
              onValueChange={(v) => setScope(v as AskPunttProjectScope)}
              size="2"
              aria-label="Filter projects by scope"
            >
              <SegmentedControl.Item
                value="mine"
                data-testid="campaigns-filter-mine"
              >
                Mine
              </SegmentedControl.Item>
              <SegmentedControl.Item
                value="shared"
                data-testid="campaigns-filter-shared"
              >
                Shared
              </SegmentedControl.Item>
              <SegmentedControl.Item
                value="all"
                data-testid="campaigns-filter-all"
              >
                All
              </SegmentedControl.Item>
            </SegmentedControl.Root>
            <Button
              data-testid="campaigns-new-button"
              onClick={() => setCreateOpen(true)}
              className="cursor-pointer"
            >
              <Plus size={14} weight="bold" /> New project
            </Button>
          </Flex>
        </Flex>

        {isLoading ? (
          <div className="flex flex-col gap-2" data-testid="campaigns-skeleton">
            {Array.from({ length: 4 }).map((_, i) => (
              <div
                key={i}
                className="h-20 animate-pulse rounded-lg border border-puntt-neutral-gray-4 bg-puntt-neutral-gray-2"
              />
            ))}
          </div>
        ) : isError ? (
          <Callout.Root color="red" size="1" data-testid="campaigns-error">
            <Callout.Text>
              Failed to load projects. Please refresh.
            </Callout.Text>
          </Callout.Root>
        ) : projects.length === 0 ? (
          <Flex
            direction="column"
            align="center"
            justify="center"
            gap="4"
            className="mt-16"
            data-testid="campaigns-empty"
          >
            <FolderSimplePlus
              size={40}
              weight="duotone"
              className="text-puntt-neutral-gray-8"
            />
            <Text
              size="4"
              weight="medium"
              className="text-puntt-neutral-gray-11"
            >
              No projects yet
            </Text>
            <Text
              size="2"
              className="max-w-sm text-center text-puntt-neutral-gray-9"
            >
              Create a project to give Puntt a shared chat, personal chats, and
              versioned documents & context for a body of work.
            </Text>
            <Button
              data-testid="campaigns-empty-new-button"
              onClick={() => setCreateOpen(true)}
              className="cursor-pointer"
            >
              <Plus size={14} weight="bold" /> New project
            </Button>
          </Flex>
        ) : (
          <div className="flex flex-col gap-2" data-testid="campaigns-list">
            {projects.map((p) => (
              <button
                key={p._id}
                type="button"
                onClick={() =>
                  navigate({
                    to: campaignRoute.to,
                    params: { projectId: p._id },
                  })
                }
                data-testid={`campaign-row-${p._id}`}
                data-project-id={p._id}
                aria-label={`Open project ${p.name}`}
                className="flex w-full cursor-pointer items-center gap-4 rounded-lg border border-puntt-neutral-gray-5 bg-base-white p-4 text-left transition-all hover:border-puntt-accent-7 hover:shadow-sm"
              >
                <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-puntt-accent-2 text-puntt-accent-9">
                  <FolderSimplePlus size={20} weight="duotone" />
                </div>
                <div className="min-w-0 flex-1">
                  <Text
                    size="3"
                    weight="medium"
                    className="block truncate text-puntt-neutral-gray-12"
                  >
                    {p.name}
                  </Text>
                  {p.description ? (
                    <Text
                      size="1"
                      className="block truncate text-puntt-neutral-gray-11"
                    >
                      {p.description}
                    </Text>
                  ) : null}
                </div>
                <Text size="1" className="shrink-0 text-puntt-neutral-gray-9">
                  {formatDate(p.updatedAt)}
                </Text>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Create project dialog */}
      <Dialog.Root open={createOpen} onOpenChange={setCreateOpen}>
        <Dialog.Content
          maxWidth="460px"
          data-testid="campaigns-create-dialog"
          aria-label="Create a project"
        >
          <Dialog.Title>New project</Dialog.Title>
          <Dialog.Description size="2" className="text-puntt-neutral-gray-11">
            Puntt creates a shared chat for the project automatically. You can
            add personal chats and documents once it&apos;s open.
          </Dialog.Description>
          <label htmlFor="campaigns-create-name" className="mt-3 block">
            <Text
              size="1"
              weight="medium"
              className="text-puntt-neutral-gray-11"
            >
              Name
            </Text>
            <TextField.Root
              id="campaigns-create-name"
              data-testid="campaigns-create-name"
              className="mt-1"
              placeholder="e.g. NESCAFÉ Ice Roast — Spring '26 Launch"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
            />
          </label>
          <label htmlFor="campaigns-create-description" className="mt-3 block">
            <Text
              size="1"
              weight="medium"
              className="text-puntt-neutral-gray-11"
            >
              Description (optional)
            </Text>
            <TextArea
              id="campaigns-create-description"
              data-testid="campaigns-create-description"
              className="mt-1"
              placeholder="What is this project about?"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              style={{ height: 72 }}
            />
          </label>
          <Flex gap="3" mt="4" justify="end">
            <Dialog.Close>
              <Button
                variant="soft"
                color="gray"
                data-testid="campaigns-create-cancel"
              >
                Cancel
              </Button>
            </Dialog.Close>
            <Button
              data-testid="campaigns-create-submit"
              disabled={!name.trim() || createProject.isPending}
              onClick={handleCreate}
            >
              {createProject.isPending ? (
                <CircleNotch size={14} weight="bold" className="animate-spin" />
              ) : null}
              Create project
            </Button>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>
    </div>
  );
}
